import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { IoPersonAddOutline, IoArrowBack } from "react-icons/io5";
import { FiSend } from "react-icons/fi";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { server } from "../server";
import useAuth from "../useAuth";
import { toast } from "react-toastify";
import LoadingComponent from "../Components/LoadingComponent";

export default function RegisterUser() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      role: "user",
      userType: "user",
      designation: "user",
      isOrganization: false,
    },
  });
  const [loading, setLoading] = useState(false);

  const handleRegisterUser = async (data) => {
    try {
      setLoading(true);
      const payload = {
        fullName: `${data.firstName.trim()} ${data.lastName.trim()}`.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
        mobileNo: Number(data.mobile) || 0,
        role: data.role || "user",
        userType: data.userType || "user",
        designation: data.designation || "user",
        organization: data.organization || "",
        isOrganization: !!data.isOrganization,
      };

      const { data: resData } = await axios.post(
        `${server}/auth/register`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(resData?.message || "User registered successfully!");
      reset();
      navigate("/my/usersList");
    } catch (e) {
      const err = e?.response?.data;
      if (typeof err === "object") {
        if (err.message) toast.error(err.message);
        else if (err.Email) toast.error(err.Email[0]);
        else toast.error("Failed to register user");
      } else {
        toast.error(err || e?.message || "Failed to register user");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-xl border border-gray-100"
      >
        {/* Top Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 text-blue-600 rounded-xl">
              <IoPersonAddOutline className="text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Register New User
              </h2>
              <p className="text-xs text-gray-500">
                Create a new user account with designated roles and permissions
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            <IoArrowBack className="text-xl" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleRegisterUser)} className="space-y-4">
          {/* First & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                First Name *
              </label>
              <input
                type="text"
                {...register("firstName", { required: "First name is required" })}
                placeholder="First name"
                className={`h-11 px-4 w-full border ${
                  errors.firstName ? "border-red-400" : "border-gray-300"
                } rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm`}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Last Name *
              </label>
              <input
                type="text"
                {...register("lastName", { required: "Last name is required" })}
                placeholder="Last name"
                className={`h-11 px-4 w-full border ${
                  errors.lastName ? "border-red-400" : "border-gray-300"
                } rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm`}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
              Email Address *
            </label>
            <input
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: "Invalid email format",
                },
              })}
              placeholder="e.g. user@company.com"
              className={`h-11 px-4 w-full border ${
                errors.email ? "border-red-400" : "border-gray-300"
              } rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm`}
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Password & Mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Password *
              </label>
              <input
                type="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Minimum 6 characters",
                  },
                })}
                placeholder="At least 6 characters"
                className={`h-11 px-4 w-full border ${
                  errors.password ? "border-red-400" : "border-gray-300"
                } rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm`}
              />
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Mobile Number *
              </label>
              <input
                type="tel"
                maxLength={10}
                {...register("mobile", {
                  required: "Mobile number is required",
                  minLength: { value: 10, message: "10 digits required" },
                })}
                placeholder="10-digit mobile"
                className={`h-11 px-4 w-full border ${
                  errors.mobile ? "border-red-400" : "border-gray-300"
                } rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm`}
              />
              {errors.mobile && (
                <p className="text-xs text-red-500 mt-1">{errors.mobile.message}</p>
              )}
            </div>
          </div>

          {/* User Type & Role */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                User Type *
              </label>
              <select
                {...register("userType", { required: true })}
                className="h-11 px-3 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm cursor-pointer"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="partner">Partner</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Role
              </label>
              <input
                type="text"
                {...register("role")}
                placeholder="e.g. user, manager"
                className="h-11 px-4 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm"
              />
            </div>
          </div>

          {/* Designation & Organization */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Designation
              </label>
              <input
                type="text"
                {...register("designation")}
                placeholder="e.g. Associate, Manager"
                className="h-11 px-4 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Organization
              </label>
              <input
                type="text"
                {...register("organization")}
                placeholder="Organization or Company"
                className="h-11 px-4 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 text-sm"
              />
            </div>
          </div>

          {/* Organization Checkbox */}
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="regIsOrg"
              {...register("isOrganization")}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="regIsOrg" className="text-sm text-gray-700 cursor-pointer">
              Is this an Organization Account?
            </label>
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className="w-full mt-4 flex items-center justify-center gap-2 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold tracking-wide shadow-md hover:shadow-lg transition duration-200"
          >
            <FiSend className="text-lg" />
            Register User
          </motion.button>
        </form>
      </motion.div>

      {loading && <LoadingComponent />}
    </div>
  );
}
